import { createImage, getImages } from 'slices/locationImageSlice'
import { createNote } from 'slices/locationNoteSlice'
import { patchLocation } from 'slices/locationSlice'
import { getExtensionFromName } from 'components/FileUploader/fileUploadUtils'
import type { ImageNoteAdapter } from '../commitImageUpload'
import type { CommitImage } from '../imageUploadWizard.types'

export interface LocationImageAdapterArgs {
	dispatch: any
	userId: string
	locationId: string
	/**
	 * Optional. The Location Images route legitimately has no project, and
	 * getMyLocationImages returns every image for the location when projectid
	 * is absent — which the portfolio rollup depends on. Do not coerce this to
	 * null to make it look tidy.
	 */
	projectId?: string
}

/** Derive the stored `type` the same way the old upload handlers did. */
export const resolveImageType = (image: CommitImage): string => {
	const name = image.normalized.name ?? image.file.name ?? 'uploaded-image'
	const fromName = getExtensionFromName(name)
	const fromMime = image.normalized.mimeType ? image.normalized.mimeType.split('/').pop() : ''
	return fromName || fromMime || 'jpg'
}

/**
 * Project and Location images. Both surfaces store LocationImage rows; a
 * project's images are simply the rows carrying its projectid.
 */
export const locationImageAdapter = ({
	dispatch,
	userId,
	locationId,
	projectId,
}: LocationImageAdapterArgs): ImageNoteAdapter => ({
	createImageRow: async ({ image, note }) => {
		await dispatch(
			(createImage as any)({
				userid: userId,
				locationid: locationId,
				projectid: projectId,
				uuid: image.upload.public_id,
				name: image.normalized.name ?? image.file.name,
				size: image.upload.bytes ?? 0,
				url: image.upload.secure_url,
				source: image.normalized.source ?? 'cloudinary',
				type: resolveImageType(image),
				note,
			}),
		).unwrap()
	},

	createNoteRow: async ({ html }) => {
		await dispatch(
			(createNote as any)({
				userid: userId,
				locationid: locationId,
				projectid: projectId,
				note: html,
				parentid: null,
			}),
		).unwrap()
	},

	afterCommit: async () => {
		dispatch(
			(patchLocation as any)({ locationId, updates: { updateNote: 'Image(s) Updated' } }),
		)
		// Refetch is mandatory: createImage.fulfilled writes entities without
		// appending to ids, so a new row is invisible until this runs.
		await dispatch(
			(getImages as any)({ locationid: locationId, projectid: projectId, userid: userId }),
		)
	},
})

export default locationImageAdapter
